// program_ui.jsx — shared building blocks for the Programs tab, the program
// editor, sharing and onboarding: tokens, icons, pills, buttons, sheets, steppers,
// week strips. No program logic lives here.

import React from 'react';
import { LLSheet } from './sheets.jsx';
import { LL, LLIcon, LLClose } from './tokens.jsx';
import { Portal } from './exercise_picker.jsx';
import { useSheetHistory } from './sheet_history.js';

export const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const kicker = { fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.2, color: LL.fgDim, textTransform: 'uppercase' };
export const card = { background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md };
export const meta = { fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, fontVariantNumeric: 'tabular-nums' };
export const tint = (c, pct = 16) => `color-mix(in oklab, ${c} ${pct}%, ${LL.surface})`;
// Icon color that stays legible on dark and light: mixes the hue toward the foreground.
export const ink = (c) => `color-mix(in oklab, ${c} 72%, ${LL.fg})`;

export const GOAL_HUE = {
  strength: 'oklch(0.80 0.16 70)',
  hypertrophy: 'var(--ll-accent, oklch(0.86 0.19 132))',
  powerbuilding: 'oklch(0.72 0.17 25)',
  general: 'oklch(0.75 0.13 230)',
};

export const fmtRest = (s) => (s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${s}s`);
export const fmtReps = (a, b) => (a === b ? `${a}` : `${a}–${b}`);

export function reducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

// One-time keyframes for this module (fade/slide-in for cards and steps).
export function ProgramStyles() {
  return (
    <style>{`
      @keyframes llpFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
      @keyframes llpSlideIn { from { opacity: 0; transform: translateX(28px); } to { opacity: 1; transform: none; } }
      @keyframes llpSlideBack { from { opacity: 0; transform: translateX(-28px); } to { opacity: 1; transform: none; } }
      @keyframes llpPop { 0% { transform: scale(.96); } 60% { transform: scale(1.02); } 100% { transform: scale(1); } }
      .llp-noscroll::-webkit-scrollbar { display: none; }
      @media (prefers-reduced-motion: reduce) { .llp-anim { animation: none !important; transition: none !important; } }
    `}</style>
  );
}

// ——— Icons (local) ——————————————————————————————————————————————————————

export const PIcon = {
  barbell: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M1.5 10h17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><rect x="3.5" y="5.5" width="2.5" height="9" rx="1" stroke="currentColor" strokeWidth="1.6"/><rect x="14" y="5.5" width="2.5" height="9" rx="1" stroke="currentColor" strokeWidth="1.6"/></svg>,
  // Flexed arm: upper arm, bicep peak, forearm up to a fist.
  muscle: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M2.5 10.6c1.2-.5 2.4-1.3 3.6-1.9 2-1 4.5-.4 5.5 1.4l-.5-2.6-.8-2.1c-.4-1.2.3-2.4 1.6-2.4 1.8 0 2.8 1.5 2.8 3.5l.5 4c.4 3-1.2 5-4.2 5H2.5v-4.9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M6.2 12.6c1.3-1 3-1 4.3.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
  bolt: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M11 2.5L4.5 11h5l-1 6.5L15.5 9h-5l.5-6.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  heart: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 16.5S3 12 3 7.5a3.5 3.5 0 017-1 3.5 3.5 0 017 1c0 4.5-7 9-7 9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M5.5 10h2l1-2 2 4 1-2h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  home: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 9l7-5.5L17 9v8h-5v-5H8v5H3V9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  body: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><circle cx="10" cy="4" r="2" stroke="currentColor" strokeWidth="1.5"/><path d="M4 8l6 1 6-1M10 9v4l-3 4.5M10 13l3 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  dumbbell: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><rect x="2" y="6.5" width="3" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="15" y="6.5" width="3" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/><path d="M5 10h10M.8 8.5v3M19.2 8.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  clock: (p) => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" {...p}><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6"/><path d="M10 6v4l2.5 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  calendar: (p) => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" {...p}><rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M3 8h14M7 2.5v3M13 2.5v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  layers: (p) => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 3l7 3.5-7 3.5-7-3.5L10 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M3 10l7 3.5 7-3.5M3 13.5L10 17l7-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  filter: (p) => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 5.5h14M5.5 10h9M8 14.5h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg>,
  sparkle: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 3l1.6 4.4L16 9l-4.4 1.6L10 15l-1.6-4.4L4 9l4.4-1.6L10 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M15.5 13.5l.7 1.6 1.6.7-1.6.7-.7 1.6-.7-1.6-1.6-.7 1.6-.7.7-1.6z" fill="currentColor"/></svg>,
  edit: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M4 16l1-4 8.5-8.5a2.1 2.1 0 013 3L8 15l-4 1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  copy: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><rect x="6.5" y="6.5" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M13.5 6.5V5a1.5 1.5 0 00-1.5-1.5H5A1.5 1.5 0 003.5 5v7A1.5 1.5 0 005 13.5h1.5" stroke="currentColor" strokeWidth="1.5"/></svg>,
  trash: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M4 6h12M8 6V4h4v2M5.5 6l.8 10h7.4l.8-10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  up: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 15V5M5.5 9.5L10 5l4.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  down: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 5v10M5.5 10.5L10 15l4.5-4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  link: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M8.5 11.5l3-3M7 9.5L5.5 11a2.5 2.5 0 003.5 3.5l1.5-1.5M13 10.5l1.5-1.5A2.5 2.5 0 0011 5.5L9.5 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  star: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 2.8l2.2 4.6 5 .6-3.7 3.4 1 5-4.5-2.5-4.5 2.5 1-5L2.8 8l5-.6L10 2.8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  plus: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg>,
  check: (p) => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" {...p}><path d="M4.5 10.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  share: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 12.5V3M6.5 6.5L10 3l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 9.5H5a1.5 1.5 0 00-1.5 1.5v5A1.5 1.5 0 005 17.5h10a1.5 1.5 0 001.5-1.5v-5A1.5 1.5 0 0015 9.5h-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  wave: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M2.5 14c2-6 4-6 5 0s3 6 5 0 3-6 5 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  bars: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 16V12M7.5 16V9.5M12 16V7M16.5 16V4.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/></svg>,
  stairs: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 16h4v-4h4V8h4V4h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  warn: (p) => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 3l8 14H2L10 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M10 8v4M10 14.5v.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
};

export function templateIcon(t) {
  if (t.equipment.length === 0) return PIcon.body;
  if (!t.equipment.includes('barbell') && !t.equipment.includes('machine') && !t.equipment.includes('cable')) return PIcon.dumbbell;
  if (t.goal === 'strength') return PIcon.barbell;
  if (t.goal === 'powerbuilding') return PIcon.bolt;
  if (t.goal === 'general') return PIcon.heart;
  return PIcon.muscle;
}

export function GoalTile({ goal, icon, size = 44 }) {
  const Icon = icon;
  const hue = GOAL_HUE[goal] || GOAL_HUE.hypertrophy;
  return (
    <div style={{
      width: size, height: size, borderRadius: LL.r.md, flexShrink: 0,
      background: tint(hue, 18), color: ink(hue),
      boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${hue} 28%, transparent)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon style={{ width: size * 0.5, height: size * 0.5 }} />
    </div>
  );
}

// 7-day strip: training days filled. `days` = ['Mon', 'Wed', ...]
export function SplitStrip({ days = [], size = 'sm' }) {
  const set = new Set(days);
  const h = size === 'lg' ? 28 : 18;
  return (
    <div style={{ display: 'flex', gap: size === 'lg' ? 6 : 3, width: size === 'lg' ? '100%' : undefined }} aria-label={`Trains ${days.join(', ')}`}>
      {WEEK.map(d => {
        const on = set.has(d);
        return (
          <div key={d} style={{
            width: size === 'lg' ? undefined : 18, flex: size === 'lg' ? 1 : undefined, maxWidth: size === 'lg' ? 52 : undefined,
            height: h, borderRadius: size === 'lg' ? LL.r.sm : LL.r.xs,
            background: on ? LL.accent : LL.surface2,
            boxShadow: on ? 'none' : `inset 0 0 0 1px ${LL.line}`,
            color: on ? LL.accentInk : LL.fgDim,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700,
          }}>{size === 'lg' ? d.slice(0, 1) : ''}</div>
        );
      })}
    </div>
  );
}

export function Pill({ children, tone = 'default', style = {} }) {
  const tones = {
    default: { bg: LL.surface2, fg: LL.fgMute, br: LL.line },
    accent: { bg: LL.accentSoft, fg: LL.accentText, br: `color-mix(in oklab, ${LL.accent} 40%, transparent)` },
    warn: { bg: `color-mix(in oklab, ${LL.warn} 14%, transparent)`, fg: `color-mix(in oklab, ${LL.warn} 70%, ${LL.fg})`, br: `color-mix(in oklab, ${LL.warn} 35%, transparent)` },
  };
  const t = tones[tone] || tones.default;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, height: 24, padding: '0 9px', borderRadius: LL.r.pill,
      background: t.bg, color: t.fg, border: `1px solid ${t.br}`,
      fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, whiteSpace: 'nowrap', ...style,
    }}>{children}</span>
  );
}

export function Btn({ children, onClick, tone = 'primary', size = 'md', full, icon, style = {}, disabled, ariaLabel }) {
  const tones = {
    primary: { bg: LL.accent, fg: LL.accentInk, br: 'transparent' },
    secondary: { bg: LL.surface, fg: LL.fg, br: LL.line2 },
    ghost: { bg: 'transparent', fg: LL.fgMute, br: 'transparent' },
    soft: { bg: LL.accentSoft, fg: LL.accentText, br: 'transparent' },
    danger: { bg: 'transparent', fg: `color-mix(in oklab, ${LL.danger} 80%, ${LL.fg})`, br: `color-mix(in oklab, ${LL.danger} 45%, ${LL.line})` },
  };
  const t = tones[tone] || tones.primary;
  const h = size === 'lg' ? 54 : size === 'sm' ? 36 : 46;
  return (
    <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled} aria-label={ariaLabel} style={{
      height: h, padding: size === 'sm' ? '0 14px' : '0 18px', borderRadius: LL.r.md,
      width: full ? '100%' : undefined,
      background: t.bg, color: t.fg, border: t.br === 'transparent' ? 'none' : `1px solid ${t.br}`,
      fontFamily: LL.ui, fontSize: size === 'lg' ? LL.fs.btn : size === 'sm' ? LL.fs.meta : LL.fs.body, fontWeight: 600,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1, whiteSpace: 'nowrap',
      transition: `transform ${LL.motion.fast}ms ${LL.ease}, background ${LL.motion.base}ms ${LL.ease}`,
      ...style,
    }}>{icon}{children}</button>
  );
}

export function IconBtn({ children, onClick, title, size = 40, tone = 'default', style = {} }) {
  const tones = {
    default: { bg: LL.surface, fg: LL.fg, br: LL.line },
    ghost: { bg: 'transparent', fg: LL.fgMute, br: 'transparent' },
    soft: { bg: LL.accentSoft, fg: LL.accentText, br: 'transparent' },
  };
  const t = tones[tone] || tones.default;
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} style={{
      width: size, height: size, borderRadius: size > 36 ? LL.r.md : LL.r.sm, flexShrink: 0, padding: 0,
      background: t.bg, color: t.fg, border: t.br === 'transparent' ? 'none' : `1px solid ${t.br}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', ...style,
    }}>{children}</button>
  );
}

// Bottom sheet rendered in a portal (above the tab bar), tall, with a sticky footer.
export function Sheet({ open, onClose, title, subtitle, children, footer, height = 'calc(100dvh - 44px)' }) {
  const [mounted, setMounted] = React.useState(open);
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setMounted(true);
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(r);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), 300);
    return () => clearTimeout(t);
  }, [open]);
  // Android / browser Back closes the sheet, like every other sheet.
  useSheetHistory(!!open, onClose);
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!mounted) return null;
  const dur = reducedMotion() ? 0 : LL.motion.slow;
  return (
    <Portal>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 200, pointerEvents: 'auto', background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        opacity: shown ? 1 : 0, transition: `opacity ${dur}ms ${LL.ease}`,
      }} />
      <div role="dialog" aria-modal="true" aria-label={title} style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 201, pointerEvents: 'auto',
        maxHeight: height, margin: '0 auto', maxWidth: 560,
        background: LL.bg, borderTopLeftRadius: LL.r.lg, borderTopRightRadius: LL.r.lg,
        border: `1px solid ${LL.line}`, borderBottom: 'none', boxShadow: LL.shadow.sheet,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        transform: shown ? 'translateY(0)' : 'translateY(100%)',
        transition: `transform ${dur}ms ${LL.ease}`,
      }}>
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 4 }}>
            <div style={{ width: 40, height: 4, borderRadius: LL.r.xs, background: LL.line2 }} />
          </div>
          {title != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0 10px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {subtitle && <div style={{ ...kicker, marginBottom: 3 }}>{subtitle}</div>}
                <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg, letterSpacing: -0.3, lineHeight: 1.1 }}>{title}</div>
              </div>
              <LLClose onClick={onClose} />
            </div>
          )}
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 20px', overscrollBehavior: 'contain' }}>
          {children}
        </div>
        {footer && (
          <div style={{ flexShrink: 0, padding: '12px 16px calc(14px + env(safe-area-inset-bottom))', borderTop: `1px solid ${LL.line}`, background: LL.bg }}>
            {footer}
          </div>
        )}
      </div>
    </Portal>
  );
}

// Action list sheet (program / day menus)
export function ActionSheet({ open, onClose, title, subtitle, items }) {
  return (
    <LLSheet open={open} onClose={onClose} title={title} subtitle={subtitle}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(items || []).filter(Boolean).map(it => {
          const danger = it.tone === 'danger';
          const fg = danger ? `color-mix(in oklab, ${LL.danger} 80%, ${LL.fg})` : LL.fg;
          return (
            <button key={it.id} type="button" onClick={it.disabled ? undefined : it.onClick} style={{
              display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: '8px 12px', borderRadius: LL.r.md,
              background: LL.surface2, border: `1px solid ${LL.line}`, color: fg, textAlign: 'left',
              fontFamily: LL.ui, cursor: it.disabled ? 'default' : 'pointer', opacity: it.disabled ? 0.45 : 1,
            }}>
              <div style={{ width: 32, height: 32, borderRadius: LL.r.sm, background: danger ? `color-mix(in oklab, ${LL.danger} 14%, ${LL.surface})` : LL.surface, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <it.icon style={{ width: 18, height: 18 }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: LL.fs.body, fontWeight: 600 }}>{it.label}</div>
                {it.sub && <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 1 }}>{it.sub}</div>}
              </div>
            </button>
          );
        })}
      </div>
    </LLSheet>
  );
}

export function StatTile({ label, value, unit }) {
  return (
    <div style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: LL.r.md, background: LL.surface, border: `1px solid ${LL.line}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
        <span style={{ fontFamily: LL.display, fontWeight: 700, fontSize: LL.fs.h2, color: LL.fg, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{value}</span>
        {unit && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim, fontWeight: 600 }}>{unit}</span>}
      </div>
      <div style={{ ...kicker, marginTop: 6, letterSpacing: 0.9 }}>{label}</div>
    </div>
  );
}

export const SCHEME_SHORT = { linear: 'Linear', percent: '% of TM', rpe_topset: 'Top set', gzcl: 'Tiered', rp_sets: 'Meso', double: 'Reps → weight' };

// Resets the nearest scrolling ancestor (the sheet body) when a new step mounts.
export function ScrollTopOnMount() {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    let el = ref.current?.parentElement;
    while (el && !(el.scrollHeight > el.clientHeight && /auto|scroll/.test(getComputedStyle(el).overflowY))) el = el.parentElement;
    if (el) el.scrollTop = 0;
  }, []);
  return <div ref={ref} aria-hidden />;
}

// "Week 2 · 3s" → "3s"; plain labels pass through.
export const weekName = (w, i) => { const l = (w && w.label) || `Week ${i + 1}`; return /^Week \d+ · /.test(l) ? l.split(' · ').slice(1).join(' · ') : l; };

export function SchemeTag({ scheme }) {
  if (!scheme || scheme === 'double') return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px', borderRadius: LL.r.sm, flexShrink: 0,
      background: LL.accentSoft, color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 0.2,
    }}>{SCHEME_SHORT[scheme] || scheme}</span>
  );
}

// Horizontal week selector. Current week gets a dot; deload weeks are muted.
export function WeekStrip({ weeks, current, selected, onSelect }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current?.querySelector(`[data-wk="${selected}"]`);
    // Scroll only the strip itself: scrollIntoView would also scroll the page's
    // overflow:hidden root and leave a blank band under the content.
    const box = ref.current;
    if (el && box && box.scrollTo) {
      const left = box.scrollLeft + el.getBoundingClientRect().left - box.getBoundingClientRect().left - (box.clientWidth - el.offsetWidth) / 2;
      box.scrollTo({ left: Math.max(0, left), behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
  }, [selected]);
  return (
    <div ref={ref} className="llp-noscroll" role="tablist" aria-label="Weeks" style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', margin: '0 -14px', padding: '2px 14px 4px' }}>
      {weeks.map((w, i) => {
        const on = i === selected;
        const cur = i === current;
        return (
          <button key={i} type="button" role="tab" aria-selected={on} data-wk={i} onClick={() => onSelect(i)} style={{
            flexShrink: 0, minWidth: 58, padding: '7px 10px 8px', borderRadius: LL.r.md, cursor: 'pointer', textAlign: 'left',
            background: on ? `color-mix(in oklab, ${LL.accent} 18%, ${LL.surface})` : (w.deload ? 'transparent' : LL.surface2),
            border: `1px ${w.deload && !on ? 'dashed' : 'solid'} ${on ? `color-mix(in oklab, ${LL.accent} 55%, transparent)` : LL.line}`,
            transition: `background ${LL.motion.base}ms ${LL.ease}, border-color ${LL.motion.base}ms ${LL.ease}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: on ? LL.accentText : LL.fgDim }}>Wk {i + 1}</span>
              {cur && <span aria-label="current week" style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: LL.accent }} />}
            </div>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, color: on ? LL.fg : LL.fgMute, marginTop: 2, whiteSpace: 'nowrap' }}>
              {w.deload ? 'Deload' : (String(w.label || '').split(' · ')[1] || w.label || `Week ${i + 1}`)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export const toDisp = (lb, units) => (units === 'kg' ? Math.round(lb * 0.45359237 * 10) / 10 : lb);
export const fromDisp = (v, units) => (units === 'kg' ? Math.round((v / 0.45359237) * 100) / 100 : v);

export function Row({ label, children, border, grow = true }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', minHeight: 58, borderTop: border ? `1px solid ${LL.line}` : 'none', boxSizing: 'border-box' }}>
      <div style={{ flex: grow ? 1 : '0 0 auto', fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fg }}>{label}</div>
      {children}
    </div>
  );
}

// Numeric field: − [typeable value] +. Commits on blur / Enter.
export function NumField({ value, onChange, min = 0, max = 9999, step = 1, suffix, width = 150, format, ariaLabel, decimals = 1, placeholder }) {
  const [draft, setDraft] = React.useState(null);
  const shown = draft != null ? draft : (format ? format(value) : (value === 0 && placeholder ? '' : String(value)));
  const clamp = (v) => Math.min(max, Math.max(min, v));
  const round = (v) => Math.round(v * 10 ** decimals) / 10 ** decimals;
  const commit = () => {
    if (draft == null) return;
    const n = parseFloat(String(draft).replace(',', '.'));
    if (!Number.isNaN(n)) onChange(clamp(round(n)));
    setDraft(null);
  };
  const bump = (dir) => onChange(clamp(round((Number(value) || 0) + dir * step)));
  const btn = {
    width: 38, height: 38, borderRadius: LL.r.md, flexShrink: 0, padding: 0,
    background: LL.surface2, color: LL.fg, border: `1px solid ${LL.line}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, width, flexShrink: 0 }}>
      <button type="button" aria-label={`Decrease ${ariaLabel || ''}`} style={btn} onClick={() => bump(-1)}><LLIcon.minus style={{ width: 18, height: 18 }} /></button>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 3 }}>
        <input
          aria-label={ariaLabel}
          inputMode="decimal"
          value={shown}
          placeholder={placeholder}
          onFocus={(e) => { setDraft(format ? String(value) : (value === 0 && placeholder ? '' : String(value))); requestAnimationFrame(() => { try { e.target.select(); } catch { /* noop */ } }); }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          style={{
            // With a suffix the value box hugs its digits so "12" and "wk" never overlap.
            width: suffix ? `${Math.max(1, String(shown || placeholder || '').length) + 0.6}ch` : '100%', minWidth: 0,
            textAlign: suffix ? 'right' : 'center', background: 'transparent', border: 'none', outline: 'none', padding: 0,
            fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg, fontVariantNumeric: 'tabular-nums',
          }}
        />
        {suffix && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: LL.fgDim, fontWeight: 600, pointerEvents: 'none', flexShrink: 0 }}>{suffix}</span>}
      </div>
      <button type="button" aria-label={`Increase ${ariaLabel || ''}`} style={btn} onClick={() => bump(1)}><LLIcon.plus style={{ width: 18, height: 18 }} /></button>
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <div style={{ padding: '8px 10px 10px', borderRadius: LL.r.md, background: LL.surface, border: `1px solid ${LL.line}` }}>
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, color: LL.fgMute, marginBottom: 6 }}>{label}</div>
      {children}
      {hint && <div style={{ ...meta, fontSize: LL.fs.cap, color: LL.fgDim, marginTop: 6 }}>{hint}</div>}
    </div>
  );
}
